import { Application, Assets, MeshSimple, Texture } from "pixi.js";

/**
 * THE MENU BACKDROP: the wind pass through the trees, and the slow breathe of the whole painting.
 *
 * 🚨 **THIS IS A LITERAL TRANSCRIPTION OF `theme/motion/RevivalTreeSway.cs` AND `theme/motion/RevivalMenuMotion.cs`, NOT A
 * NEW EFFECT.** Alex, 2026-08-14: *"please do not lose our beautiful background page with the exact
 * motion in the trees and the Revival title that we finally got perfectly."* Those constants are the
 * product of many rounds with him - `Amplitude` went 0.006 → 0.016 → 0.030 → 0.042 → 0.055 on his
 * notes *"the trees still do not sway"*, then *"sway more"*, then *"flatten and widen it"* - and the
 * depth term was rewritten twice before the shape was right. **Re-tuning any number here throws that
 * away.** If the motion needs to change, change it in the C# owner first and re-transcribe, until
 * Phase 3 retires the C# and this becomes the owner.
 *
 * ⭐ **WHY A MESH AND NOT A CSS TRANSFORM.** The trees are painted INTO one backdrop image; there is
 * no tree object to rotate. A first pass here used two separate tree PNGs with a CSS rotate, which is
 * exactly the "approximate it" failure the project has a memory about - it is not the same picture
 * and not the same motion. The only faithful way is to move the pixels, which means displacing a
 * grid over the image. `Cols`/`Rows` and every coefficient below are the originals.
 *
 * ⚠️ **THE MOON MUST NOT MOVE.** It sits at u≈0.63 and `weight()` is zero for |u-0.5| < 0.5-EdgeBand
 * = 0.20, so nothing between u 0.30 and 0.70 is displaced at any amplitude. `verify_web_motion`
 * asserts this the same way the Unity gate did: the treelines move, the moon does not.
 *
 * ⚠️ **UNITY'S v IS UP, THE DOM'S y IS DOWN.** `v` is flipped when sampling and the vertical
 * displacement is negated, because "back into the screen is drawn as UP, toward the horizon" in the
 * original and up is negative y here. Getting this wrong inverts the egg and reads as the canopy
 * lunging at the player, which is the exact note Alex gave on the Unity version.
 */

// ── theme/motion/RevivalTreeSway.cs, verbatim ────────────────────────────────────────────────────────────────
const COLS = 14;
const ROWS = 10;
const AMPLITUDE = 0.055;
const VERTICAL_RATIO = 0.12;
const EDGE_BAND = 0.3;
const ROOT_HEIGHT = 0.22;
const RIM_PIN = 0.05;

// ── theme/motion/RevivalMenuMotion.cs kind 0 (the backdrop breathe), verbatim ────────────────────────────────
// ⚠️ These are canvas units against the Lobby scene's 1000x600 reference, so they are applied here
// as the SAME FRACTIONS of the viewport rather than as raw pixels. 9 of 1000 wide, 6 of 600 high.
const BREATHE_X_FRAC = 9 / 1000;
const BREATHE_Y_FRAC = 6 / 600;

function inverseLerp(a: number, b: number, v: number): number {
  return a === b ? 0 : Math.min(1, Math.max(0, (v - a) / (b - a)));
}

/** Two unrelated periods plus a phase that travels with height, so a gust moves through the canopy. */
function wind(u: number, v: number, t: number): number {
  const a = Math.sin(t * 0.55 + u * 5.1 + v * 2.3);
  const b = Math.sin(t * 0.23 + u * 2.7 - v * 1.1);
  return a * 0.65 + b * 0.35;
}

/** Zero through the centre (the moon), rising toward both edges, zero at the roots and pinned at the rim. */
function weight(u: number, v: number): number {
  const edge = inverseLerp(0.5 - EDGE_BAND, 0.5, Math.abs(u - 0.5));
  if (edge <= 0) return 0;
  const high = inverseLerp(ROOT_HEIGHT, 1, v);
  const rim = Math.min(1, Math.max(0, Math.min(u, 1 - u) / RIM_PIN));
  // Squared so the canopy dominates and the transition out of the still zone is not a seam.
  return edge * edge * high * high * rim;
}

/* 🛑 **A "SECOND ROW" SWAY WAS ADDED HERE ON 2026-08-18 AND REVERTED THE SAME DAY.**
 *
 * Alex asked: *"can, without breaking the existing tree movement, add some tree movement to these
 * second row trees?"* I read "second row" as the ridge TREELINE and released the whole centre band
 * below the moon. He then said *"I meant just 2 trees"*, and on seeing it live: *"now it looks like
 * the trees in the background are changed and ugly. Please go back to the original background."*
 *
 * ⭐ **THE MISTAKE WAS SCOPE, NOT MATHS.** The change did exactly what it claimed - the gate proved
 * the foreground was bit-identical and the moon still frozen - and it was still wrong, because
 * "these trees" in a screenshot is a POINTER, not a category, and I widened it into one. A whole
 * moving treeline reads as the painting wobbling; two swaying trees would have read as wind.
 *
 * ⚠️ **IF THIS IS PICKED UP AGAIN: get the two trees identified on the artwork FIRST** (they are
 * individual firs with visible bare trunks, not the ridge line), and displace only those two
 * patches. Do not reintroduce a band. Recorded in `docs/QUEUE.md`.
 */

export interface Backdrop {
  destroy(): void;
}

/**
 * Mount the animated backdrop into `host`, behind everything else.
 *
 * ⚠️ Returns a handle whose `destroy()` MUST be called on unmount. A Pixi ticker that outlives its
 * screen is a leak that shows up as the menu getting progressively slower every time you return to
 * it - a class of bug the old client had in its signal handlers.
 */
export async function mountBackdrop(host: HTMLElement): Promise<Backdrop> {
  const app = new Application();
  await app.init({
    resizeTo: host,
    backgroundAlpha: 0,
    antialias: true,
    // ⚠️ Cap the device pixel ratio. A 3x phone screen would otherwise render a full-screen mesh at
    // nine times the pixels for no visible gain, and the backdrop is a soft painting.
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });

  const canvas = app.canvas as HTMLCanvasElement;
  canvas.className = "menu__sky";
  host.prepend(canvas);

  // 🚨 **CSS OWNS THE CANVAS'S LAYOUT SIZE. PIXI OWNS ONLY THE BACKING STORE.**
  //
  // `autoDensity` writes an INLINE `style.width/height` in CSS pixels, and an inline style beats the
  // stylesheet's `.menu__sky { inset: 0; width: 100%; height: 100% }` outright - specificity, not
  // cascade order. So whatever size Pixi measured ONCE becomes the backdrop's size forever.
  //
  // ⛔ **AND IT MEASURES AT THE WORST POSSIBLE MOMENT.** `mountBackdrop(el)` is called from
  // `menu.ts` while `el` is still DETACHED - the screen is built and appended afterwards - so
  // `resizeTo: host` reads a `clientWidth` of 0. `resizeTo` then only re-measures on a WINDOW
  // resize; it has no ResizeObserver. A player who never resizes their window keeps the size taken
  // before the element existed.
  //
  // ⚠️ Alex sent a screenshot of the live menu with the painting rendered into a small box in one
  // corner while the plates were laid out correctly across the whole window. **I could not
  // reproduce it from here** - fresh loads, eight viewports, six remounts and a live resize all
  // tracked the stage exactly. That does not clear the construction: "I could not make it happen"
  // is not "it cannot happen", and this is a first-frame timing race, which is precisely the kind
  // that reproduces on someone else's machine and not on the one driving the harness.
  //
  // So the race is removed rather than argued with: the inline size is cleared so the stylesheet
  // governs, and the renderer's backing store follows the element via a ResizeObserver.
  const trackHost = () => {
    const w = host.clientWidth, h = host.clientHeight;
    if (w > 0 && h > 0) app.renderer.resize(w, h);
    (window as unknown as { __revivalBackdrop?: { w: number; h: number } }).__revivalBackdrop =
      { w: Math.round(app.screen.width), h: Math.round(app.screen.height) };
    // Cleared AFTER the resize, because `autoDensity` re-writes them on every resize.
    canvas.style.removeProperty("width");
    canvas.style.removeProperty("height");
  };
  trackHost();
  const hostObserver = new ResizeObserver(trackHost);
  hostObserver.observe(host);

  // ⭐ **THE DRAWING'S OWN SIZE, PUBLISHED FOR THE HARNESS.** A gate comparing DOM rectangles cannot
  // see inside a canvas - it certified this screen as correct while the painting occupied a quarter
  // of it. This is the one number that would have failed.
  const publish = () => {
    (window as unknown as { __revivalBackdrop?: { w: number; h: number } }).__revivalBackdrop =
      { w: Math.round(app.screen.width), h: Math.round(app.screen.height) };
  };
  publish();

  const texture: Texture = await Assets.load("/art/menu/backdrop.png");

  const stride = COLS + 1;
  const vertCount = stride * (ROWS + 1);
  const positions = new Float32Array(vertCount * 2);
  const uvs = new Float32Array(vertCount * 2);
  const indices: number[] = [];

  for (let r = 0; r <= ROWS; r++) {
    for (let c = 0; c <= COLS; c++) {
      const i = r * stride + c;
      uvs[i * 2] = c / COLS;
      uvs[i * 2 + 1] = r / ROWS;
    }
  }
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const i = r * stride + c;
      indices.push(i, i + stride, i + stride + 1, i, i + stride + 1, i + 1);
    }
  }

  const mesh = new MeshSimple({ texture, vertices: positions, uvs, indices: new Uint32Array(indices) });
  app.stage.addChild(mesh);

  const start = performance.now() / 1000;

  const layout = () => {
    // 🚨 **`app.screen`, NOT `renderer.width / resolution`. THAT DIVISION HALVED THE PAINTING ON
    // EVERY RETINA DISPLAY AND WAS INVISIBLE TO EVERY CHECK WE HAD.**
    //
    // In Pixi v8 `renderer.screen` is already the LOGICAL size in CSS pixels. Dividing it by the
    // resolution again applies the device pixel ratio a second time, so on a 2x display the mesh was
    // laid out for an 800x450 viewport inside a canvas that was correctly 1600x900 - the painting
    // rendered into the top-left quarter and the rest of the screen was black.
    //
    // ⛔ **AT dPR 1 THE DIVISION IS BY 1 AND THE BUG DOES NOT EXIST.** The CDP harness runs at
    // dPR 1, so every screenshot I ever took was of the healthy case, and the DOM measurements were
    // healthy too - stage, menu and canvas ELEMENT all matched exactly. Alex was the only
    // instrument that could see it, from a laptop screen. See `tools/verify_layout_sizes.py`.
    //
    // ⚠️ The general rule, because this will recur: **a canvas has two sizes and they fail
    // independently.** The ELEMENT's CSS box and the CONTENT drawn inside it. Every check we had
    // measured the box.
    const vw = app.screen.width;
    const vh = app.screen.height;

    // ⭐ COVER, NEVER LETTERBOX. The Unity build showed slate bands top and bottom at 16:9 because
    // the art did not fill the canvas and the camera's clear colour showed through. Scaling to the
    // LARGER ratio and centring cannot produce that at any aspect.
    //
    // ⚠️ The breathe's scale is 1.012 + sin(...)*0.008, i.e. ALWAYS above 1. In the original that is
    // load-bearing: "any value under 1.0 exposes the canvas edge as a hairline gap down one side...
    // it would pass every screenshot and fail in use." The same is true here, so the base cover
    // scale is multiplied by it rather than replaced.
    const cover = Math.max(vw / texture.width, vh / texture.height);
    return { vw, vh, cover };
  };

  const tick = () => {
    const { vw, vh, cover } = layout();
    const t = performance.now() / 1000 - start;

    // RevivalMenuMotion kind 0: two sines at incommensurable periods so the drift never repeats,
    // and a scale that never dips below 1.
    //
    // 🚨 **THE AMPLITUDE IS THE FRACTION. DO NOT MULTIPLY BY THE CANVAS-UNIT COUNT AS WELL.**
    // The first version wrote `sin(...) * 9` and then multiplied that by `vw * (9/1000)`, applying
    // the 9 TWICE - a 8.1%-of-width drift instead of 0.9%. Alex, immediately: *"the motion is wrong,
    // the entire screen is moving. In our original version that I approved just the trees are
    // swaying."* He is right: at the correct amplitude this is ~1% over a ~133-second period and is
    // imperceptible as motion; it exists only so the painting is never quite static.
    //
    // ⚠️ This is `engineering-invariants` §3b.8 exactly - **a proportional value cannot hold a
    // constant**. `BREATHE_X_FRAC` already IS "9 canvas units of 1000"; the sine is unit-amplitude.
    const breatheX = Math.sin(t * 0.0471);
    const breatheY = Math.cos(t * 0.0331);
    const breatheS = 1.012 + Math.sin(t * 0.0233) * 0.008;

    const scale = cover * breatheS;
    const w = texture.width * scale;
    const h = texture.height * scale;
    const originX = (vw - w) / 2 + breatheX * vw * BREATHE_X_FRAC;
    const originY = (vh - h) / 2 + breatheY * vh * BREATHE_Y_FRAC;

    const amp = w * AMPLITUDE;

    for (let r = 0; r <= ROWS; r++) {
      // ⚠️ THE FLIP. Unity's v is 0 at the BOTTOM; row 0 here is the TOP of the image.
      const fvUnity = 1 - r / ROWS;
      for (let c = 0; c <= COLS; c++) {
        const fu = c / COLS;
        const i = r * stride + c;

        const sway = wind(fu, fvUnity, t) * weight(fu, fvUnity);
        const dx = sway * amp;
        // ⭐ A HALF OVAL, NOT A FULL ELLIPSE - furthest AWAY through centre, nearest at both extremes.
        // `1 - |sway|` is exactly that curve, and `|sway|` alone is the same curve upside down, which
        // was the earlier attempt and read as the canopy looming forward.
        // Negated because "away" is drawn as UP, and up is -y in screen space.
        const dy = -(1 - Math.abs(sway)) * amp * VERTICAL_RATIO;

        positions[i * 2] = originX + fu * w + dx;
        positions[i * 2 + 1] = originY + (r / ROWS) * h + dy;
      }
    }

    // Push the new vertex positions to the GPU.
    const buf = mesh.geometry.getBuffer("aPosition");
    buf.data = positions;
    buf.update();
  };

  app.ticker.add(tick);
  tick();

  return {
    destroy() {
      app.ticker.remove(tick);
      // ⚠️ Disconnected BEFORE the app is destroyed. A live observer firing `app.renderer.resize`
      // on a destroyed renderer throws from a resize callback, which is a place nothing is
      // listening - the same class of leak the ticker note above describes.
      hostObserver.disconnect();
      canvas.remove();
      app.destroy(true, { children: true });
    },
  };
}

/**
 * THE WORDMARK DRIFT. Wispy, not a bob.
 *
 * 🚨 **THIS DELIBERATELY DIVERGES FROM `RevivalMenuMotion` KIND 1, ON ALEX'S INSTRUCTION.** That was
 * `y = sin(t * 0.9) * 3` - a pure vertical bob on a 7-second period. Alex, 2026-08-14: *"the Revival
 * title is choppy, it should be a smooth motion, and it is not up and down it is wavy, or whispy
 * motion."* Unity is being retired, so this file is now the owner of that motion; the C# is history,
 * not a target to match.
 *
 * ⭐ **WHY IT WAS CHOPPY, AND IT WAS NOT THE FRAME RATE.** The old amplitude was ~4.6px over a
 * 7-second period, which is roughly one pixel per second - and a transform that small gets quantised
 * to whole pixels unless the element is on its own compositor layer. So it advanced in visible
 * steps. `translate3d` + `will-change: transform` promotes it and the browser interpolates in
 * sub-pixels. The rounding to two decimals is also gone: at this speed it was a real part of the
 * staircase.
 *
 * ⭐ **WISPY IS TWO UNRELATED FREQUENCIES PER AXIS, PLUS A TOUCH OF ROTATION.** One sine per axis
 * traces an ellipse and reads as a mechanical orbit. Summing two at incommensurable periods gives a
 * path that never repeats, and a very small rotation makes it read as something SUSPENDED in air
 * rather than sliding on a plane. Same principle as the wind in the canopy above, which is two waves
 * for the same reason.
 *
 * ⚠️ **AMPLITUDES SCALE WITH THE VIEWPORT**, like every other layout number here, so the motion is
 * the same gesture on a phone and on a monitor.
 */
export function floatWordmark(el: HTMLElement): () => void {
  el.style.willChange = "transform";
  const start = performance.now() / 1000;
  let raf = 0;

  const step = () => {
    const t = performance.now() / 1000 - start;
    const k = window.innerHeight / 900; // the approval's own reference height

    // Two waves per axis at unrelated periods (~48s/20s and ~33s/90s), so the path never repeats.
    const x = (Math.sin(t * 0.13) * 3.2 + Math.sin(t * 0.31) * 1.1) * k;
    const y = (Math.sin(t * 0.19) * 2.4 + Math.cos(t * 0.07) * 1.6) * k;
    const rot = Math.sin(t * 0.11) * 0.18;

    // ⚠️ translate3d, NOT translate: it is what puts this on its own layer. And no toFixed - the
    // sub-pixel precision is the difference between a drift and a staircase.
    el.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${rot}deg)`;
    raf = requestAnimationFrame(step);
  };

  raf = requestAnimationFrame(step);
  return () => {
    cancelAnimationFrame(raf);
    el.style.willChange = "";
  };
}
